/**
 * Type definitions for Server-Sent Events (SSE) in the knowledge graph chat feature.
 * @remarks
 * Provides discriminated union types and type guards for SSE events received from
 * the chat API. These types ensure type safety when parsing and handling different
 * event types during streaming chat responses.
 *
 * Event types include:
 * - status: Agent status updates (thinking, searching, analyzing)
 * - token: Streaming text tokens for chat response
 * - complete: Query completion signal
 * - start: Query start signal
 * - end: Query end signal
 * - error: Error events
 * - citations: Citation metadata for sources
 */

/**
 * Base interface for all SSE events.
 */
interface BaseSSEEvent {
  /** Event type discriminator */
  readonly type: string;
  /** Event content/message - backend uses 'message' field for status events */
  readonly content?: string;
  /** Event message - used by status events */
  readonly message?: string;
  /** Unix timestamp when event was created */
  readonly timestamp?: number;
}

/**
 * Agent status update event.
 * @remarks
 * Sent during query processing to indicate what the agent is currently doing
 * (e.g., "Thinking...", "Searching...", "Analyzing...").
 */
export interface StatusEvent extends BaseSSEEvent {
  readonly type: "status";
  /** Agent name (e.g., "decide", "generateSQLWithLangChain") */
  readonly agent?: string;
  /** Status message */
  readonly message?: string;
  /** Stage of processing */
  readonly stage?: "start" | "progress" | "complete" | "error";
  /** Additional data */
  readonly data?: unknown;
}

/**
 * Streaming text token event.
 * @remarks
 * Contains a chunk of the AI response text. Multiple token events are combined
 * to form the complete response message.
 */
export interface TokenEvent extends BaseSSEEvent {
  readonly type: "token";
}

/**
 * Query completion event.
 * @remarks
 * Signals that the AI has finished processing the query and all response tokens
 * have been sent.
 */
export interface CompleteEvent extends BaseSSEEvent {
  readonly type: "complete";
}

/**
 * Query start event.
 * @remarks
 * Signals that the AI has started processing a new query.
 */
export interface StartEvent extends BaseSSEEvent {
  readonly type: "start";
  /**
   * The id ask-gemini lists this conversation under (`constructThreadId`),
   * which is NOT the session id the client sent (SE-8865).
   */
  readonly sessionId?: string;
}

/**
 * Query end event.
 * @remarks
 * Signals the end of the current query processing session.
 */
export interface EndEvent extends BaseSSEEvent {
  readonly type: "end";
}

/**
 * Error event.
 * @remarks
 * Sent when an error occurs during query processing. Content contains the
 * error message.
 */
export interface ErrorEvent extends BaseSSEEvent {
  readonly type: "error";
}

/**
 * Citation data for a single source.
 */
export interface Citation {
  /** Source URL */
  readonly url: string;
  /** Source title */
  readonly title: string;
}

/**
 * Type guard to check if a value is a valid Citation object.
 * @param value - Value to check
 * @returns True if value is a valid Citation with url and title strings
 */
const isCitation = (value: unknown): value is Citation => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const citation = value as Record<string, unknown>;

  return (
    typeof citation.url === "string" &&
    citation.url.length > 0 &&
    typeof citation.title === "string" &&
    citation.title.length > 0
  );
};

/**
 * Citations event.
 * @remarks
 * Contains metadata about sources cited in the AI response. Can be used to
 * display references or allow users to explore source materials.
 */
export interface CitationsEvent extends BaseSSEEvent {
  readonly type: "citations";
  /** Array of citation metadata */
  readonly data: readonly Citation[];
}

/**
 * Discriminated union of all possible SSE event types.
 */
export type SSEEvent =
  | StatusEvent
  | TokenEvent
  | CompleteEvent
  | StartEvent
  | EndEvent
  | ErrorEvent
  | CitationsEvent;

/**
 * Type guard to check if a value is a valid SSE event.
 * @param value - Value to check
 * @returns True if value is a valid SSE event
 */
export const isSSEEvent = (value: unknown): value is SSEEvent => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const event = value as Record<string, unknown>;

  // Check required type field
  if (typeof event.type !== "string") {
    return false;
  }

  // Check valid event types
  const validTypes = [
    "status",
    "token",
    "complete",
    "start",
    "end",
    "error",
    "citations",
  ];

  if (!validTypes.includes(event.type)) {
    return false;
  }

  // Status events use 'message' field, others use 'content'
  // Both are optional now to handle backend variations
  if (event.content !== undefined && typeof event.content !== "string") {
    return false;
  }

  if (event.message !== undefined && typeof event.message !== "string") {
    return false;
  }

  // Timestamp is optional
  if (event.timestamp !== undefined && typeof event.timestamp !== "number") {
    return false;
  }

  // Additional validation for citations event
  if (event.type === "citations") {
    return (
      event.data !== undefined &&
      Array.isArray(event.data) &&
      event.data.every(isCitation)
    );
  }

  return true;
};

/**
 * Type guard to check if an event is a status event.
 * @param event - Event to check
 * @returns True if event is a status event
 */
export const isStatusEvent = (event: unknown): event is StatusEvent =>
  isSSEEvent(event) && event.type === "status";

/**
 * Type guard to check if an event is a token event.
 * @param event - Event to check
 * @returns True if event is a token event
 */
export const isTokenEvent = (event: unknown): event is TokenEvent =>
  isSSEEvent(event) && event.type === "token";

/**
 * Type guard to check if an event is a complete event.
 * @param event - Event to check
 * @returns True if event is a complete event
 */
export const isCompleteEvent = (event: unknown): event is CompleteEvent =>
  isSSEEvent(event) && event.type === "complete";

/**
 * Type guard to check if an event is a start event.
 * @param event - Event to check
 * @returns True if event is a start event
 */
export const isStartEvent = (event: unknown): event is StartEvent =>
  isSSEEvent(event) && event.type === "start";

/**
 * Type guard to check if an event is an end event.
 * @param event - Event to check
 * @returns True if event is an end event
 */
export const isEndEvent = (event: unknown): event is EndEvent =>
  isSSEEvent(event) && event.type === "end";

/**
 * Type guard to check if an event is an error event.
 * @param event - Event to check
 * @returns True if event is an error event
 */
export const isErrorEvent = (event: unknown): event is ErrorEvent =>
  isSSEEvent(event) && event.type === "error";

/**
 * Type guard to check if an event is a citations event.
 * @param event - Event to check
 * @returns True if event is a citations event with valid citation objects
 */
export const isCitationsEvent = (event: unknown): event is CitationsEvent =>
  isSSEEvent(event) &&
  event.type === "citations" &&
  "data" in event &&
  Array.isArray(event.data) &&
  event.data.every(isCitation);
